import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SEO from "../components/common/SEO";
import PageHero from "../components/common/PageHero";
import SectionHeading from "../components/common/SectionHeading";
import { Reveal, StaggerGroup, StaggerItem } from "../components/common/Reveal";
import ResumeForm from "../components/forms/ResumeForm";
import CTASection from "../components/common/CTASection";
import PillButton from "../components/common/PillButton";
import SaveJobButton from "../components/careers/SaveJobButton";
import JobAlerts from "../components/careers/JobAlerts";
import { MapPin, Briefcase, ArrowUpRight, Search, X } from "lucide-react";
import { useContent } from "../context/ContentContext";
import { getToken, getSaved, addSaved, removeSaved } from "../lib/api";
import { WORK_AUTH_OPEN } from "../lib/workAuth";
const perks = [
  { title: "Meaningful Work", body: "Work on high-impact technology and engineering initiatives across leading industries." },
  { title: "Growth & Learning", body: "Access to modern tooling, continuous learning and a network of specialists." },
  { title: "Flexible Engagements", body: "Contract, contract-to-hire and permanent opportunities that fit your goals." },
  { title: "Responsive Partnership", body: "Recruiters who advocate for you and keep you informed throughout." },
];

export default function Careers() {
  const [selectedRole, setSelectedRole] = useState("");
  const [savedSlugs, setSavedSlugs] = useState([]);
  const [query, setQuery] = useState("");
  const [fType, setFType] = useState("");
  const [fSkill, setFSkill] = useState("");
  const [fLocation, setFLocation] = useState("");
  const [fWorkAuth, setFWorkAuth] = useState("");
  const { jobs } = useContent();
  const navigate = useNavigate();

  const uniq = (key) => Array.from(new Set(jobs.map((j) => j[key]).filter(Boolean))).sort();
  const types = useMemo(() => uniq("type"), [jobs]);
  const skills = useMemo(() => uniq("skill"), [jobs]);
  const locations = useMemo(() => uniq("location"), [jobs]);
  const workAuths = useMemo(() => uniq("work_authorization"), [jobs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return jobs.filter((j) => {
      if (fType && j.type !== fType) return false;
      if (fSkill && j.skill !== fSkill) return false;
      if (fLocation && j.location !== fLocation) return false;
      if (fWorkAuth && j.work_authorization !== fWorkAuth && j.work_authorization !== WORK_AUTH_OPEN) return false;
      if (q) {
        const hay = `${j.role || ""} ${j.summary || ""} ${j.skill || ""} ${j.location || ""} ${j.type || ""} ${j.work_authorization || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [jobs, query, fType, fSkill, fLocation, fWorkAuth]);

  const hasFilters = query || fType || fSkill || fLocation || fWorkAuth;
  const clearFilters = () => {
    setQuery("");
    setFType("");
    setFSkill("");
    setFLocation("");
    setFWorkAuth("");
  };

  useEffect(() => {
    if (!getToken()) return;
    getSaved().then((d) => setSavedSlugs(d.saved || [])).catch(() => {});
  }, []);

  const toggleSave = async (slug) => {
    if (!getToken()) {
      toast.info("Sign in to save jobs", { description: "Create a portal session to bookmark roles." });
      navigate("/portal/login");
      return;
    }
    try {
      if (savedSlugs.includes(slug)) {
        const res = await removeSaved(slug);
        setSavedSlugs(res.saved || []);
      } else {
        const res = await addSaved(slug);
        setSavedSlugs(res.saved || []);
        toast.success("Job saved");
      }
    } catch {
      toast.error("Could not update saved jobs");
    }
  };

  const applyTo = (role) => {
    setSelectedRole(role);
    const el = document.getElementById("submit-resume");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <SEO
        title="Careers"
        description="Build what's next with AMVI Technologies. Explore open technology and engineering roles and submit your resume."
        path="/careers"
      />
      <PageHero
        eyebrow="Careers"
        title="Build What's Next With AMVI"
        subtitle="Join a network of technology and engineering professionals working on meaningful, high-impact initiatives."
        image="https://images.unsplash.com/photo-1531482615713-2afd69097998?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
      >
        <PillButton to="/portal/login" variant="outline" testId="careers-portal-link">Track Your Application</PillButton>
      </PageHero>

      <section className="px-6 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeading eyebrow="Open Positions" title="Current opportunities" testId="careers-openings" />

          <div className="mt-10 rounded-2xl border border-white/10 bg-ink-800/40 p-4 sm:p-5" data-testid="careers-filters">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search roles, skills or keywords…"
                className="w-full rounded-xl border border-white/10 bg-white/[0.03] py-3 pl-11 pr-4 text-sm text-white outline-none focus:border-sky-400/70"
                data-testid="careers-search"
              />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <select
                value={fType}
                onChange={(e) => setFType(e.target.value)}
                className="rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
                data-testid="careers-filter-type"
              >
                <option value="" className="bg-ink-900">All types</option>
                {types.map((t) => (
                  <option key={t} value={t} className="bg-ink-900">{t}</option>
                ))}
              </select>
              <select
                value={fSkill}
                onChange={(e) => setFSkill(e.target.value)}
                className="rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
                data-testid="careers-filter-skill"
              >
                <option value="" className="bg-ink-900">All skills</option>
                {skills.map((s) => (
                  <option key={s} value={s} className="bg-ink-900">{s}</option>
                ))}
              </select>
              <select
                value={fLocation}
                onChange={(e) => setFLocation(e.target.value)}
                className="rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
                data-testid="careers-filter-location"
              >
                <option value="" className="bg-ink-900">All locations</option>
                {locations.map((l) => (
                  <option key={l} value={l} className="bg-ink-900">{l}</option>
                ))}
              </select>
              <select
                value={fWorkAuth}
                onChange={(e) => setFWorkAuth(e.target.value)}
                className="rounded-lg border border-white/10 bg-ink-900 px-3 py-2 text-sm text-white outline-none focus:border-sky-400/70"
                data-testid="careers-filter-work-auth"
              >
                <option value="" className="bg-ink-900">All work authorizations</option>
                {workAuths.map((w) => (
                  <option key={w} value={w} className="bg-ink-900">{w}</option>
                ))}
              </select>
              {hasFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white"
                  data-testid="careers-filter-clear"
                >
                  <X className="h-3.5 w-3.5" /> Clear
                </button>
              )}
              <span className="ml-auto text-sm text-slate-500" data-testid="careers-result-count">
                {filtered.length} {filtered.length === 1 ? "role" : "roles"}
              </span>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="mt-8 rounded-2xl border border-white/10 bg-ink-800/40 p-10 text-center" data-testid="careers-no-results">
              <p className="text-slate-300">No roles match your search.</p>
              <button onClick={clearFilters} className="mt-3 text-sm text-sky-400 hover:text-sky-300">Clear filters</button>
            </div>
          ) : (
          <StaggerGroup className="mt-8 grid gap-4 md:grid-cols-2">
            {filtered.map((o) => (
              <StaggerItem key={o.slug}>
                <div className="relative">
                  <Link
                    to={`/careers/${o.slug}`}
                    className="group flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-ink-800/40 p-6 pr-14 transition-colors hover:border-sky-400/40"
                    data-testid={`opening-${o.slug}`}
                  >
                    <div>
                      <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-sky-400">
                        <Briefcase className="h-3 w-3" /> {o.type}
                      </div>
                      <h3 className="font-heading text-lg font-bold tracking-tight text-white group-hover:text-sky-300">{o.role}</h3>
                      <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-400">
                        <MapPin className="h-3.5 w-3.5" /> {o.location}
                      </p>
                      {o.work_authorization ? (
                        <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-2.5 py-1 text-[11px] font-medium text-emerald-300" data-testid={`opening-work-auth-${o.slug}`}>
                          {o.work_authorization}
                        </span>
                      ) : null}
                    </div>
                    <span className="hidden shrink-0 items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm text-white transition-colors group-hover:border-sky-400/60 group-hover:text-sky-400 sm:inline-flex">
                      View & Apply
                      <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </Link>
                  <div className="absolute right-4 top-4">
                    <SaveJobButton
                      saved={savedSlugs.includes(o.slug)}
                      onClick={() => toggleSave(o.slug)}
                      testId={`opening-save-${o.slug}`}
                    />
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
          )}

          <div className="mt-8">
            <JobAlerts skills={skills} />
          </div>
        </div>
      </section>

      <section className="border-t border-white/5 px-6 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl">
          <SectionHeading eyebrow="Life at AMVI" title="Why join us" testId="careers-perks" />
          <StaggerGroup className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {perks.map((p) => (
              <StaggerItem key={p.title}>
                <div className="h-full rounded-2xl border border-white/10 bg-ink-800/40 p-7">
                  <h3 className="font-heading text-lg font-bold tracking-tight text-white">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{p.body}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      <section id="submit-resume" className="border-t border-white/5 px-6 py-16 lg:py-24">
        <div className="mx-auto max-w-3xl">
          <SectionHeading eyebrow="Submit Resume" title="Submit Your Resume" subtitle="Not sure which role fits? Share your profile and our recruiting team will match you with the right opportunities." testId="careers-resume" />
          <Reveal className="mt-12 rounded-2xl border border-white/10 bg-ink-800/50 p-8 lg:p-10">
            <ResumeForm initialRole={selectedRole} />
          </Reveal>
        </div>
      </section>

      <CTASection title="Looking to hire instead?" subtitle="Submit a staffing requirement and we'll help you build your team." />
    </>
  );
}
